import { apiHandler } from '@/services/apiHandler';
import { lotteParkingAxios } from '@/services/axiosInstances';
import type { AreaInfoApiResponse, AreaInfoRequest, BranchParkingConfig, CarParkingListRequest, ParkingApiResponse, ParkingFeeApiResponse, ParkingFeeRequest, ParkingInApiResponse } from '@/services/dto/parking';
import { PARKING_ENDPOINTS } from '@/services/endpoints/parking';
import type { ApiResponse } from '@/types/api';

/**
 * Get parking information for a specific branch
 */
export const getParkingInfo = async (branchCode: string): Promise<ApiResponse<BranchParkingConfig>> => {
    const response = await apiHandler(async () => await lotteParkingAxios.get(`${PARKING_ENDPOINTS.PARKING_INFO}/${branchCode}`, {
        headers: {
            'apiUsprKey': import.meta.env.VITE_LOTTE_API_USPR_KEY,
            'apiAthrKey': import.meta.env.VITE_LOTTE_API_ATHR_KEY_EIF_APP_020,
            'apiId': import.meta.env.VITE_LOTTE_PARKING_API_ID
        }
    }));

    if (response.ok && response.data) {
        const { resultCode, resultMsg, ...parkingData } = response.data as unknown as ParkingApiResponse;

        const branchData = parkingData[branchCode];
        if (typeof branchData === 'object' && branchData !== null) {
            return {
                ...response,
                data: branchData as BranchParkingConfig
            };
        }
    }
    throw new Error(`No parking data found for branch: ${branchCode}`);
};

/**
 * Get car parking list by site code and car number
 */
export const getCarParkingList = async (request: CarParkingListRequest): Promise<ApiResponse<ParkingInApiResponse>> => {
    const response = await apiHandler(async () => {
        const axiosResponse = await lotteParkingAxios.post(PARKING_ENDPOINTS.GET_CAR_PARKING_LIST, request, {
            headers: {
                'apiUserKey': import.meta.env.VITE_LOTTE_PARKING_API_USER_KEY_POST,
                'apiAthrKey': import.meta.env.VITE_LOTTE_PARKING_API_ATHR_KEY_USER_POST,
                'Content-Type': 'application/json'
            }
        });
        return axiosResponse;
    });

    if (response.ok && response.data) {
        return {
            ...response,
            data: response.data as unknown as ParkingInApiResponse
        };
    }

    return response;
};

/**
 * Get parking fee for a specific car
 */
export const getParkingFee = async (request: ParkingFeeRequest): Promise<ApiResponse<ParkingFeeApiResponse>> => {
    const response = await apiHandler(async () => {
        const axiosResponse = await lotteParkingAxios.post(PARKING_ENDPOINTS.GET_PARKING_FEE, request, {
            headers: {
                'apiUserKey': import.meta.env.VITE_LOTTE_PARKING_API_USER_KEY_POST ,
                'apiAthrKey': import.meta.env.VITE_LOTTE_PARKING_API_ATHR_KEY_USER_POST ,
                'Content-Type': 'application/json'
            }
        });
        return axiosResponse;
    });

    if (response.ok && response.data) {
        return {
            ...response,
            data: response.data as unknown as ParkingFeeApiResponse
        };
    }

    return response;
};

/**
 * Get area info for a specific car
 */
export const getAreaInfo = async (request: AreaInfoRequest): Promise<ApiResponse<AreaInfoApiResponse>> => {
    const response = await apiHandler(async () => {
        const axiosResponse = await lotteParkingAxios.post(PARKING_ENDPOINTS.GET_AREA_INFO, request, {
            headers: {
                'apiUserKey': import.meta.env.VITE_LOTTE_PARKING_API_USER_KEY_POST,
                'apiAthrKey': import.meta.env.VITE_LOTTE_PARKING_API_ATHR_KEY_USER_POST,
                'Content-Type': 'application/json'
            }
        });
        return axiosResponse;
    });

    if (response.ok && response.data) {
        return {
            ...response,
            data: response.data as unknown as AreaInfoApiResponse
        };
    }

    return response;
};
